import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { platformKeysApi } from "@/api/platform-keys";
import { modelPolicyApi } from "@/api/models";
import { imageGenApi, type ImageGenSettings } from "@/api/image-gen";
import { apiFetch } from "@/api/client";
import { Key, Globe, Github, Bot, Network, CheckCircle, XCircle, Upload, Loader2, Layers, Image as ImageIcon } from "lucide-react";

/** Centrally-managed model policy: estate-wide backup model + platform functions model. */
function ModelPolicySection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["model-policy"], queryFn: modelPolicyApi.get });
  const [backup, setBackup] = useState("");
  const [fns, setFns] = useState("");
  const [assistant, setAssistant] = useState("");
  useEffect(() => {
    if (data) {
      setBackup(data.defaultBackupModel ?? "");
      setFns(data.functionsModel ?? "");
      setAssistant(data.assistantModel ?? "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      modelPolicyApi.update({
        defaultBackupModel: backup || null,
        functionsModel: fns || null,
        assistantModel: assistant || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["model-policy"] }),
  });

  const models = data?.models ?? [];
  // Scout's tool loop speaks the Anthropic Messages format, so only direct
  // Anthropic models or Foundry Claude deployments are offered.
  const assistantModels = models.filter((m) => m.id.toLowerCase().includes("claude"));
  const selectCls =
    "mt-1 w-full rounded-md border border-border bg-bg-secondary px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent";

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Layers size={18} />
          Model Policy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-text-secondary">
          Manage models centrally across the estate. The <span className="font-medium">default backup</span> is the
          fallback every agent uses when its primary errors — unless that agent sets its own backup (which wins).
        </p>
        <div>
          <label className="text-sm font-medium">Estate default backup model</label>
          <select className={selectCls} value={backup} onChange={(e) => setBackup(e.target.value)}>
            <option value="">— none —</option>
            {models.map((m) => (
              <option key={`b-${m.id}`} value={m.id}>{m.label} ({m.provider})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Platform functions model</label>
          <p className="text-xs text-text-muted">Used by workspace/skill generation, onboarding, goal enrichment.</p>
          <select className={selectCls} value={fns} onChange={(e) => setFns(e.target.value)}>
            <option value="">— direct Anthropic (default) —</option>
            {models.map((m) => (
              <option key={`f-${m.id}`} value={m.id}>{m.label} ({m.provider})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Scout help assistant model</label>
          <p className="text-xs text-text-muted">
            Powers the floating help assistant on tenant pages. Claude models only — direct
            Anthropic or a Foundry Claude deployment.
          </p>
          <select className={selectCls} value={assistant} onChange={(e) => setAssistant(e.target.value)}>
            <option value="">— direct Anthropic claude-sonnet-4-6 (default) —</option>
            {assistantModels.map((m) => (
              <option key={`a-${m.id}`} value={m.id}>{m.label} ({m.provider})</option>
            ))}
          </select>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? (<><Loader2 size={14} className="mr-2 animate-spin" />Saving…</>) : "Save policy"}
        </Button>
        {save.isSuccess && <span className="ml-3 text-xs text-green-600">Saved.</span>}
      </CardContent>
    </Card>
  );
}

/**
 * Avatar image generation — pick the provider (Azure Foundry gpt-image
 * deployment or Google Gemini) and manage its endpoint/deployment/key.
 * Used by the "Generate avatar" action on agents.
 */
function AvatarImageGenSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["image-gen-settings"], queryFn: imageGenApi.get });
  const [provider, setProvider] = useState<ImageGenSettings["provider"]>("azure_foundry");
  const [endpoint, setEndpoint] = useState("");
  const [deployment, setDeployment] = useState("");
  const [foundryKey, setFoundryKey] = useState("");
  const [googleKey, setGoogleKey] = useState("");
  useEffect(() => {
    if (data) {
      setProvider(data.provider);
      setEndpoint(data.foundryEndpoint);
      setDeployment(data.foundryDeployment);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      imageGenApi.update({
        provider,
        foundryEndpoint: endpoint,
        foundryDeployment: deployment,
        ...(foundryKey ? { foundryApiKey: foundryKey } : {}),
        ...(googleKey ? { googleApiKey: googleKey } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-gen-settings"] });
      setFoundryKey("");
      setGoogleKey("");
    },
  });

  const selectCls =
    "mt-1 w-full rounded-md border border-border bg-bg-secondary px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent";

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ImageIcon size={18} />
          Avatar Image Generation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-text-secondary">
          Which image model generates agent avatars. Switch providers here without a redeploy.
        </p>
        <div>
          <label className="text-sm font-medium">Provider</label>
          <select
            className={selectCls}
            value={provider}
            onChange={(e) => setProvider(e.target.value as ImageGenSettings["provider"])}
          >
            <option value="azure_foundry">Azure Foundry (gpt-image deployment)</option>
            <option value="google">Google Gemini</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Foundry endpoint</label>
          <p className="text-xs text-text-muted">OpenAI-compatible base URL of the Foundry resource.</p>
          <Input
            className="mt-1"
            placeholder="https://<resource>.services.ai.azure.com/openai/v1"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Foundry deployment name</label>
          <Input
            className="mt-1"
            placeholder="gpt-image-2"
            value={deployment}
            onChange={(e) => setDeployment(e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium">Foundry API key</label>
            {data && (
              <KeyStatusBadge
                configured={data.foundryApiKey.configured}
                source={data.foundryApiKey.configured ? "database" : null}
              />
            )}
          </div>
          <Input
            type="password"
            placeholder={data?.foundryApiKey.configured ? `Current: ${data.foundryApiKey.masked}` : "Foundry resource key"}
            value={foundryKey}
            onChange={(e) => setFoundryKey(e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium">Google AI API key</label>
            {data && (
              <KeyStatusBadge
                configured={data.googleApiKey.configured}
                source={data.googleApiKey.configured ? "database" : null}
              />
            )}
          </div>
          <p className="text-xs text-text-muted mb-1.5">Only used when the provider is Google Gemini.</p>
          <Input
            type="password"
            placeholder={data?.googleApiKey.configured ? `Current: ${data.googleApiKey.masked}` : "AIza..."}
            value={googleKey}
            onChange={(e) => setGoogleKey(e.target.value)}
          />
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? (<><Loader2 size={14} className="mr-2 animate-spin" />Saving…</>) : "Save image settings"}
        </Button>
        {save.isSuccess && <span className="ml-3 text-xs text-green-600">Saved.</span>}
        {save.isError && (
          <span className="ml-3 text-xs text-red-500">
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

function KeyStatusBadge({ configured, source }: { configured: boolean; source: string | null }) {
  if (!configured) {
    return (
      <Badge variant="outline" className="gap-1">
        <XCircle size={12} />
        Not configured
      </Badge>
    );
  }
  return (
    <Badge variant="success" className="gap-1">
      <CheckCircle size={12} />
      Configured ({source})
    </Badge>
  );
}

export function PlatformSettings() {
  const queryClient = useQueryClient();
  const [braveApiKey, setBraveApiKey] = useState("");
  const [githubToken, setGithubToken] = useState("");
  const [anthropicApiKey, setAnthropicApiKey] = useState("");
  const [tailscaleAuthKey, setTailscaleAuthKey] = useState("");

  const { data: keys } = useQuery({
    queryKey: ["platform-keys"],
    queryFn: platformKeysApi.getKeys,
  });

  const saveMutation = useMutation({
    mutationFn: platformKeysApi.saveKeys,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-keys"] });
      setBraveApiKey("");
      setGithubToken("");
      setAnthropicApiKey("");
      setTailscaleAuthKey("");
    },
  });

  const hasChanges = braveApiKey || githubToken || anthropicApiKey || tailscaleAuthKey;

  function handleSave() {
    const data: Record<string, string> = {};
    if (braveApiKey) data.braveApiKey = braveApiKey;
    if (githubToken) data.githubToken = githubToken;
    if (anthropicApiKey) data.anthropicApiKey = anthropicApiKey;
    if (tailscaleAuthKey) data.tailscaleAuthKey = tailscaleAuthKey;
    saveMutation.mutate(data);
  }

  return (
    <CenterPanel title="Platform Settings">
      <p className="text-sm text-text-secondary mb-6">
        API keys configured here are applied to all newly deployed agents. Use "Push Config to All Agents" below to update existing running agents.
      </p>

      <ModelPolicySection />

      <AvatarImageGenSection />

      {/* Agent Tool Profile */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot size={18} />
            Agent Tool Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-text-secondary space-y-2">
            <p>
              All agents are deployed with <code className="px-1.5 py-0.5 bg-bg-secondary rounded text-xs font-mono">tools.profile: "full"</code> which
              gives access to web_search, web_fetch, exec, read, write, image, memory_search, and more.
            </p>
            <p>
              The <code className="px-1.5 py-0.5 bg-bg-secondary rounded text-xs font-mono">exec</code> tool is explicitly
              allowed via <code className="px-1.5 py-0.5 bg-bg-secondary rounded text-xs font-mono">tools.alsoAllow</code>.
            </p>
            <p>
              Web fetch is enabled by default (no API key required). Web search requires a Brave API key (below).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Core API Keys */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Key size={18} />
            Core API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Bot size={14} />
                Anthropic API Key
              </label>
              {keys && <KeyStatusBadge configured={keys.anthropicApiKey.configured} source={keys.anthropicApiKey.source} />}
            </div>
            <p className="text-xs text-text-muted mb-1.5">Required for agent LLM access and workspace generation.</p>
            <Input
              type="password"
              placeholder={keys?.anthropicApiKey.configured ? `Current: ${keys.anthropicApiKey.masked}` : "sk-ant-..."}
              value={anthropicApiKey}
              onChange={(e) => setAnthropicApiKey(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Network size={14} />
                Tailscale Auth Key
              </label>
              {keys && <KeyStatusBadge configured={keys.tailscaleAuthKey.configured} source={keys.tailscaleAuthKey.source} />}
            </div>
            <p className="text-xs text-text-muted mb-1.5">Enables agent mesh networking via Tailscale.</p>
            <Input
              type="password"
              placeholder={keys?.tailscaleAuthKey.configured ? `Current: ${keys.tailscaleAuthKey.masked}` : "tskey-auth-..."}
              value={tailscaleAuthKey}
              onChange={(e) => setTailscaleAuthKey(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tool API Keys */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe size={18} />
            Agent Tool API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Globe size={14} />
                Brave Search API Key
              </label>
              {keys && <KeyStatusBadge configured={keys.braveApiKey.configured} source={keys.braveApiKey.source} />}
            </div>
            <p className="text-xs text-text-muted mb-1.5">
              Enables web_search for all agents. Free tier: 1 request/second.{" "}
              <a href="https://brave.com/search/api/" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">
                Get API key
              </a>
            </p>
            <Input
              type="password"
              placeholder={keys?.braveApiKey.configured ? `Current: ${keys.braveApiKey.masked}` : "BSA..."}
              value={braveApiKey}
              onChange={(e) => setBraveApiKey(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Github size={14} />
                GitHub Token
              </label>
              {keys && <KeyStatusBadge configured={keys.githubToken.configured} source={keys.githubToken.source} />}
            </div>
            <p className="text-xs text-text-muted mb-1.5">
              Installs and authenticates the GitHub CLI (gh) on code-related agent VMs.
            </p>
            <Input
              type="password"
              placeholder={keys?.githubToken.configured ? `Current: ${keys.githubToken.masked}` : "ghp_..."}
              value={githubToken}
              onChange={(e) => setGithubToken(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving..." : "Save API Keys"}
        </Button>
        {saveMutation.isSuccess && (
          <Badge variant="success">
            Saved: {saveMutation.data?.updated.join(", ")}
          </Badge>
        )}
        {saveMutation.isError && (
          <Badge variant="danger">
            {saveMutation.error instanceof Error ? saveMutation.error.message : "Save failed"}
          </Badge>
        )}
      </div>

      {/* Push to Agents */}
      <PushToAgentsCard />
    </CenterPanel>
  );
}

// --- Push Platform Config to All Running Agents ---

interface SyncResult {
  success: boolean;
  total: number;
  succeeded: number;
  failed: number;
  results: Array<{ agentId: string; name: string; success: boolean; error?: string }>;
}

function PushToAgentsCard() {
  const pushMutation = useMutation({
    mutationFn: () =>
      apiFetch<SyncResult>("/agents/sync-platform-config", {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload size={18} />
          Push to Running Agents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-text-secondary mb-3">
          Push current API keys (Brave, GitHub) to all running agents. Use this after updating keys above to apply changes without redeploying.
        </p>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => pushMutation.mutate()}
            disabled={pushMutation.isPending}
            variant="outline"
          >
            {pushMutation.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1.5" />
            )}
            Push Config to All Agents
          </Button>
          {pushMutation.data && (
            <Badge variant={pushMutation.data.succeeded > 0 ? "success" : "danger"}>
              {pushMutation.data.succeeded}/{pushMutation.data.total} agents updated
            </Badge>
          )}
          {pushMutation.isError && (
            <Badge variant="danger">
              {pushMutation.error instanceof Error ? pushMutation.error.message : "Push failed"}
            </Badge>
          )}
        </div>
        {pushMutation.data && pushMutation.data.results.length > 0 && (
          <div className="mt-3 space-y-1">
            {pushMutation.data.results.map((r) => (
              <div key={r.agentId} className="flex items-center gap-2 text-xs">
                {r.success ? (
                  <CheckCircle size={12} className="text-green-500" />
                ) : (
                  <XCircle size={12} className="text-red-500" />
                )}
                <span className="text-text-primary">{r.name}</span>
                {r.error && <span className="text-text-muted">- {r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
